import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Shield, RefreshCw, Search, Trash2, Plus, Save, ToggleLeft, ToggleRight } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { AppCatalogItem } from '../types';
import { adminCreateApp, adminDeleteApp, adminListApps, adminUpdateApp } from '../services/api';
import { flagSrc } from '../utils/flags';

const normalizeAppId = (v: string) => (String(v || '').trim().toUpperCase());
const normalizeCC = (v?: string) => {
  const s = String(v || '').trim().toLowerCase();
  return s && s.length === 2 ? s : '';
};

const buildDirectLogo = (appId: string) =>
  `https://app.rewardguru.limited/api-5.0/app_logo?app_id=${encodeURIComponent(appId)}&img_size=Logo2`;

const AdminApps: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_superuser === 1;

  const [items, setItems] = useState<AppCatalogItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [q, setQ] = useState<string>('');

  const [newAppId, setNewAppId] = useState<string>('');
  const [newCC, setNewCC] = useState<string>('');
  const [newLabel, setNewLabel] = useState<string>('');
  const [newEnabled, setNewEnabled] = useState<boolean>(true);
  const [creating, setCreating] = useState<boolean>(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListApps(q.trim() ? q.trim() : undefined);
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(detail ? String(detail) : 'Не удалось загрузить приложения');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const sorted = useMemo(() => {
    return [...items].sort((a, b) => String(a.app_id || '').localeCompare(String(b.app_id || '')));
  }, [items]);

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-6 h-6 text-[#B1B5C3]" />
          <div className="text-xl font-semibold">Админка</div>
        </div>
        <div className="text-[#B1B5C3]">Недостаточно прав.</div>
      </div>
    );
  }

  const doCreate = async () => {
    const app_id = normalizeAppId(newAppId);
    const country_code = normalizeCC(newCC) || undefined;
    if (!app_id) {
      toast.error('Введите app_id');
      return;
    }
    if (newCC.trim() && !country_code) {
      toast.error('country_code должен быть ISO2 (например: ru, us)');
      return;
    }
    setCreating(true);
    try {
      const created = await adminCreateApp({
        app_id,
        country_code,
        label: newLabel.trim() ? newLabel.trim() : undefined,
        enabled: !!newEnabled,
      });
      setItems((prev) => [...prev, created]);
      setNewAppId('');
      setNewCC('');
      setNewLabel('');
      setNewEnabled(true);
      toast.success('Приложение добавлено');
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(detail ? String(detail) : 'Не удалось добавить');
    } finally {
      setCreating(false);
    }
  };

  const doSave = async (it: AppCatalogItem) => {
    const appId = normalizeAppId(it.app_id);
    const cc = it.country_code ? normalizeCC(it.country_code) : '';
    if (it.country_code && !cc) {
      toast.error('country_code должен быть ISO2 (например: ru, us)');
      return;
    }
    try {
      const updated = await adminUpdateApp(appId, {
        country_code: cc ? cc : null,
        label: it.label ? it.label : null,
        enabled: it.enabled,
      });
      setItems((prev) => prev.map((x) => (normalizeAppId(x.app_id) === appId ? updated : x)));
      toast.success('Сохранено');
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(detail ? String(detail) : 'Не удалось сохранить');
    }
  };

  const doToggleEnabled = async (it: AppCatalogItem) => {
    const appId = normalizeAppId(it.app_id);
    const nextEnabled = !it.enabled;
    // Оптимистично обновляем UI
    setItems((prev) => prev.map((x) => (normalizeAppId(x.app_id) === appId ? { ...x, enabled: nextEnabled } : x)));
    try {
      const updated = await adminUpdateApp(appId, { enabled: nextEnabled });
      setItems((prev) => prev.map((x) => (normalizeAppId(x.app_id) === appId ? updated : x)));
      toast.success(nextEnabled ? 'Включено' : 'Выключено');
    } catch (e: any) {
      // Откат
      setItems((prev) => prev.map((x) => (normalizeAppId(x.app_id) === appId ? { ...x, enabled: it.enabled } : x)));
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(detail ? String(detail) : 'Не удалось сохранить');
    }
  };

  const doSaveOnBlur = async (it: AppCatalogItem) => {
    // Без кнопки "Сохранить": сохраняем country_code/label по blur/Enter
    await doSave(it);
  };

  const doDelete = async (it: AppCatalogItem) => {
    const appId = normalizeAppId(it.app_id);
    const ok = window.confirm(`Удалить app_id ${appId}?`);
    if (!ok) return;
    try {
      await adminDeleteApp(appId);
      setItems((prev) => prev.filter((x) => normalizeAppId(x.app_id) !== appId));
      toast.success('Удалено');
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(detail ? String(detail) : 'Не удалось удалить');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-blue-400" />
          <div className="text-xl font-semibold">Админка — Приложения</div>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#23262F] border border-[#353945] text-white hover:bg-[#2b2f3a] transition"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Обновить
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-[#B1B5C3] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск: app_id / label"
            className="w-full pl-10 pr-3 py-2 rounded-lg bg-[#23262F] border border-[#353945] text-white outline-none focus:border-blue-500"
          />
        </div>
        <button
          onClick={load}
          className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 transition text-white"
          disabled={loading}
        >
          Найти
        </button>
      </div>

      {/* Create form */}
      <div className="border border-[#23262F] rounded-xl p-4 mb-4 bg-[#181A20]">
        <div className="text-sm text-white font-semibold mb-3 flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Добавить app_id
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            value={newAppId}
            onChange={(e) => setNewAppId(e.target.value)}
            placeholder="app_id (например: 9C9D...)"
            className="px-3 py-2 rounded-lg bg-[#23262F] border border-[#353945] text-white outline-none focus:border-blue-500 font-mono"
          />
          <input
            value={newCC}
            onChange={(e) => setNewCC(e.target.value)}
            placeholder="country_code (ru/us/gb)"
            className="px-3 py-2 rounded-lg bg-[#23262F] border border-[#353945] text-white outline-none focus:border-blue-500"
          />
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="label (опционально)"
            className="px-3 py-2 rounded-lg bg-[#23262F] border border-[#353945] text-white outline-none focus:border-blue-500"
          />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setNewEnabled((v) => !v)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#23262F] border border-[#353945] text-white hover:bg-[#2b2f3a] transition"
              title="Показывать в настройках"
            >
              {newEnabled ? <ToggleRight className="w-4 h-4 text-green-400" /> : <ToggleLeft className="w-4 h-4 text-[#B1B5C3]" />}
              {newEnabled ? 'Включено' : 'Выключено'}
            </button>
            <button
              type="button"
              onClick={doCreate}
              disabled={creating}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 transition text-white disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {creating ? 'Добавление…' : 'Добавить'}
            </button>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="border border-[#23262F] rounded-xl overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-[1000px] w-full text-sm">
            <thead className="bg-[#141416]">
              <tr className="text-left text-[#B1B5C3]">
                <th className="px-4 py-3">app_id</th>
                <th className="px-4 py-3">Иконка</th>
                <th className="px-4 py-3">Флаг</th>
                <th className="px-4 py-3">country_code</th>
                <th className="px-4 py-3">label</th>
                <th className="px-4 py-3">enabled</th>
                <th className="px-4 py-3">Действия</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-4 py-4 text-[#B1B5C3]" colSpan={7}>Загрузка…</td>
                </tr>
              ) : sorted.length === 0 ? (
                <tr>
                  <td className="px-4 py-4 text-[#B1B5C3]" colSpan={7}>app_id не найдены</td>
                </tr>
              ) : (
                sorted.map((it) => {
                  const appId = normalizeAppId(it.app_id);
                  const cc = normalizeCC(it.country_code);
                  return (
                    <tr key={appId} className="border-t border-[#23262F]">
                      <td className="px-4 py-3 text-white font-mono">{appId}</td>
                      <td className="px-4 py-3">
                        <img
                          src={buildDirectLogo(appId)}
                          alt="app"
                          className="w-9 h-9 object-contain rounded bg-[#141416] border border-[#23262F]"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            try { (e.currentTarget as HTMLImageElement).style.opacity = '0.2'; } catch {}
                          }}
                        />
                      </td>
                      <td className="px-4 py-3">
                        {cc ? <img src={flagSrc(cc)} alt="" className="w-6 h-4 rounded-sm border border-[#23262F]" /> : <span className="text-[#B1B5C3]">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <input
                          value={it.country_code || ''}
                          onChange={(e) => setItems((prev) => prev.map((x) => (normalizeAppId(x.app_id) === appId ? { ...x, country_code: e.target.value } : x)))}
                          onBlur={() => doSaveOnBlur(it)}
                          onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur(); }}
                          className="w-28 px-2 py-1 rounded bg-[#23262F] border border-[#353945] text-white outline-none focus:border-blue-500"
                          placeholder="ru"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          value={it.label || ''}
                          onChange={(e) => setItems((prev) => prev.map((x) => (normalizeAppId(x.app_id) === appId ? { ...x, label: e.target.value } : x)))}
                          onBlur={() => doSaveOnBlur(it)}
                          onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur(); }}
                          className="w-full px-2 py-1 rounded bg-[#23262F] border border-[#353945] text-white outline-none focus:border-blue-500"
                          placeholder="—"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => doToggleEnabled(it)}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#23262F] border border-[#353945] text-white hover:bg-[#2b2f3a] transition"
                        >
                          {it.enabled ? <ToggleRight className="w-4 h-4 text-green-400" /> : <ToggleLeft className="w-4 h-4 text-[#B1B5C3]" />}
                          {it.enabled ? 'Да' : 'Нет'}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#23262F] border border-[#353945] hover:bg-[#2b2f3a] transition text-white"
                            onClick={() => doDelete(it)}
                            title="Удалить"
                          >
                            <Trash2 className="w-4 h-4" />
                            Удалить
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="md:hidden">
          {loading ? (
            <div className="p-4 text-[#B1B5C3]">Загрузка…</div>
          ) : sorted.length === 0 ? (
            <div className="p-4 text-[#B1B5C3]">app_id не найдены</div>
          ) : (
            <div className="p-3 space-y-3">
              {sorted.map((it) => {
                const appId = normalizeAppId(it.app_id);
                const cc = normalizeCC(it.country_code);
                return (
                  <div key={appId} className="bg-[#181A20] border border-[#23262F] rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <img
                          src={buildDirectLogo(appId)}
                          alt="app"
                          className="w-10 h-10 object-contain rounded bg-[#141416] border border-[#23262F] flex-shrink-0"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            try { (e.currentTarget as HTMLImageElement).style.opacity = '0.2'; } catch {}
                          }}
                        />
                        <div className="text-white font-mono truncate">{appId}</div>
                      </div>
                      {cc ? <img src={flagSrc(cc)} alt="" className="w-7 h-5 rounded-sm border border-[#23262F]" /> : null}
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      <input
                        value={it.country_code || ''}
                        onChange={(e) => setItems((prev) => prev.map((x) => (normalizeAppId(x.app_id) === appId ? { ...x, country_code: e.target.value } : x)))}
                        onBlur={() => doSaveOnBlur(it)}
                        onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur(); }}
                        className="w-full px-3 py-2 rounded bg-[#23262F] border border-[#353945] text-white outline-none focus:border-blue-500"
                        placeholder="country_code (ru/us)"
                      />
                      <input
                        value={it.label || ''}
                        onChange={(e) => setItems((prev) => prev.map((x) => (normalizeAppId(x.app_id) === appId ? { ...x, label: e.target.value } : x)))}
                        onBlur={() => doSaveOnBlur(it)}
                        onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur(); }}
                        className="w-full px-3 py-2 rounded bg-[#23262F] border border-[#353945] text-white outline-none focus:border-blue-500"
                        placeholder="label"
                      />
                      <button
                        type="button"
                        onClick={() => doToggleEnabled(it)}
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#23262F] border border-[#353945] text-white hover:bg-[#2b2f3a] transition"
                      >
                        {it.enabled ? <ToggleRight className="w-4 h-4 text-green-400" /> : <ToggleLeft className="w-4 h-4 text-[#B1B5C3]" />}
                        {it.enabled ? 'Включено' : 'Выключено'}
                      </button>
                      <button
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#23262F] border border-[#353945] hover:bg-[#2b2f3a] transition text-white"
                        onClick={() => doDelete(it)}
                      >
                        <Trash2 className="w-4 h-4" />
                        Удалить
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminApps;

